import * as i0 from '@angular/core';
import { Injectable, Component, Inject, HostListener, NgModule } from '@angular/core';
import * as i6 from '@angular/common';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { distinctUntilChanged, filter, takeUntil, map, delay } from 'rxjs/operators';
import { Subject, BehaviorSubject, Observable, combineLatest } from 'rxjs';
import * as i1 from '@angular/common/http';
import { HttpHeaders } from '@angular/common/http';
import * as i1$1 from '@angular/router';

/**
 * Check if object contains any property from array passed by parameter
 * @param array Array of properties
 * @param object Object to check properties
 * @returns boolean It's true when any property in object
 */
function checkPropertiesInObject(array, object) {
    return array.filter(property => property in object).length > 0;
}
/**
 * Returns a promise with a setTimeout function
 * @param miliseconds Miliseconds in number format
 * @returns Promise<any>
 */
function sleep(miliseconds) {
    return new Promise(resolve => setTimeout(resolve, miliseconds));
}
/**
 * Search property parametrized includes to agent's phrases. If not search, replace it with empty string
 * @param phrase string. Phrase with parameter with format: '{parameter}'
 * @param propertySearch string
 * @param propertyReplace string
 */
function phraseReplace(phrase, propertySearch, propertyReplace) {
    const propertySearchFound = phrase.includes(`{${propertySearch}}`);
    const propertyReplaceNotUndefined = propertyReplace !== undefined;
    const replacePropertyFoundByPropertyReplace = phrase.replace(`{${propertySearch}}`, propertyReplace);
    const removeParameter = phrase.replace(/\{\w+\}/, '');
    return propertySearchFound && propertyReplaceNotUndefined
        ? replacePropertyFoundByPropertyReplace
        : removeParameter;
}

const HALF_SECOND = 500;
const ONE_SECOND = 1000;
const TWO_SECOND = 2000;
const THREE_SECOND = 3000;
const TIMEOUT_RECOGNITION = 25000;

class NgxAgentService {
    listenSubject = new Subject();
    speakSubject = new Subject();
    detectIntentSubject = new BehaviorSubject(false);
    constructor() { }
    /**
     * To Listen notifications about change state from agent service
     * @returns Observable<NgAssistant> that take notifications about change state from agent
     */
    listenStream() {
        return this.listenSubject.asObservable();
    }
    speakStream() {
        return this.speakSubject.asObservable();
    }
    detecIntentStream() {
        return this.detectIntentSubject.asObservable();
    }
    detecting(bool) {
        this.detectIntentSubject.next(bool);
    }
    speaking(message) {
        this.speakSubject.next({
            speaking: true,
            text: message
        });
    }
    stopSpeaking() {
        this.speakSubject.next({
            speaking: false
        });
    }
    listening() {
        this.listenSubject.next({
            listening: true
        });
    }
    stopListening() {
        this.listenSubject.next({
            listening: false
        });
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: NgxAgentService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: NgxAgentService });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: NgxAgentService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return []; } });

class NgxTutorialService {
    tutoSubject = new BehaviorSubject({ complete: false });
    constructor() { }
    /**
     * Get Observable to see tutorial state
     * @returns Observable Tutorial Object
     */
    getTutorialStream() {
        return this.tutoSubject.asObservable();
    }
    tutorialCompleted(bool) {
        this.tutoSubject.next({
            complete: bool
        });
    }
    tutorialIncompleteAction(action) {
        this.tutoSubject.next({
            complete: false,
            action
        });
    }
    startTutorial(bool) {
        this.tutoSubject.next({
            start: bool,
            complete: false,
        });
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: NgxTutorialService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: NgxTutorialService });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: NgxTutorialService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return []; } });

/**
 * Service to handle the state control of microphone
 */
class NgxMicrophoneService {
    state = new BehaviorSubject({
        active: true
    });
    /**
     * Get Observable to see microphone state
     * @returns Observable Microphone Object
     */
    getMicrophoneStream() {
        return this.state.asObservable();
    }
    /**
     * Send Microphone.active = true to your observers
     */
    activateMicrophone(stream) {
        this.state.next({
            active: true,
            stream
        });
    }
    /**
     * Send Microphone.active = false to your observers
     */
    desactivateMicrophone(stream) {
        this.state.next({
            active: false,
            stream
        });
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: NgxMicrophoneService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: NgxMicrophoneService });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: NgxMicrophoneService, decorators: [{
            type: Injectable
        }] });

/**
 * Service to track the conversation messages
 */
class NgxConversationService {
    /**
     * Holds the messages sent by the user
     */
    user = new Subject();
    /**
     * Holds the messages sent by the agent
     */
    agent = new Subject();
    /**
     * Stream of user utterances
     */
    getUserStream() {
        return this.user.asObservable();
    }
    /**
     * Stream of agent responses
     */
    getAgentStream() {
        return this.agent.asObservable();
    }
    /**
     * Passes new messages to user stream
     */
    userSaid(text) {
        this.user.next({
            text,
            source: 'user'
        });
    }
    /**
     * Passes new messages to the agent stream
     */
    agentSaid(text) {
        this.agent.next({
            text,
            source: 'agent'
        });
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: NgxConversationService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: NgxConversationService });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: NgxConversationService, decorators: [{
            type: Injectable
        }] });

class NgxNotificationService {
    notificationSubject = new Subject();
    getNotificationStream() {
        return this.notificationSubject.asObservable();
    }
    pushNotification(notification) {
        this.notificationSubject.next(notification);
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: NgxNotificationService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: NgxNotificationService });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: NgxNotificationService, decorators: [{
            type: Injectable
        }] });

/**
 * Reproduce audio file from path passed by parameter
 * @param src Path audio file
 */
function playAudio(src) {
    const audio = new Audio(src);
    audio.play();
}

class NgxConfigService {
    config;
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: NgxConfigService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: NgxConfigService, providedIn: 'root' });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: NgxConfigService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }] });

var SpeechInfo;
(function (SpeechInfo) {
    SpeechInfo["onStart"] = "speak_now";
    SpeechInfo["interimTranscipt"] = "interim_transcript";
    SpeechInfo["finalTranscript"] = "final_transcript";
    SpeechInfo["onEnd"] = "info_start";
})(SpeechInfo || (SpeechInfo = {}));
var SpeechError;
(function (SpeechError) {
    SpeechError["NoSpeech"] = "No speech has been detected. Please try again.";
    SpeechError["NoMicrophone"] = "Microphone is not available. Please verify the connection of your microphone and try again.";
    SpeechError["NotAllowed"] = "Your browser is not authorized to access your microphone. Verify that your browser has access to your microphone and try again.";
    SpeechError["Blocked"] = "Microphone blocked";
})(SpeechError || (SpeechError = {}));

/**
 * Service for handling speech-to-text using the web SpeechRecognition API
 */
class NgSpeechToTextService {
    recognition;
    language;
    startTimestamp;
    ignoreOnEnd;
    constructor() { }
    initialize(language = 'es-ES') {
        const { webkitSpeechRecognition } = window;
        this.recognition = new webkitSpeechRecognition() || new SpeechRecognition();
        this.recognition.continuous = true;
        this.recognition.interimResults = true;
        this.language = language;
        this.recognition.lang = language;
    }
    setLanguage(language) {
        this.language = language;
    }
    start(timestamp = Date.now()) {
        this.startTimestamp = timestamp;
        this.recognition.start();
    }
    /**
     * Deal with onStart event to SpeechRecognition API
     *
     * @return Observable<SpeechNotification> Return SpeechNotification with info about start
     */
    onStart() {
        if (!this.recognition) {
            this.initialize(this.language);
        }
        return new Observable(observer => {
            this.recognition.onstart = event => {
                observer.next({
                    info: SpeechInfo.onStart
                });
            };
        });
    }
    /**
     *
     */
    onEnd() {
        return new Observable(observer => {
            this.recognition.onend = () => {
                if (this.ignoreOnEnd) {
                    return;
                }
                observer.next({ info: SpeechInfo.onEnd });
            };
        });
    }
    onResult() {
        return new Observable(observer => {
            this.recognition.onresult = (event) => {
                let interimTranscript = '';
                let finalTranscript = '';
                for (let i = event.resultIndex; i < event.results.length; ++i) {
                    if (event.results[i].isFinal) {
                        finalTranscript += event.results[i][0].transcript;
                    }
                    else {
                        interimTranscript += event.results[i][0].transcript;
                    }
                }
                observer.next({
                    info: SpeechInfo.finalTranscript,
                    content: finalTranscript
                });
                observer.next({
                    info: SpeechInfo.interimTranscipt,
                    content: interimTranscript
                });
            };
        });
    }
    onError() {
        return new Observable(observer => {
            this.recognition.onerror = (event) => {
                const result = this.transformResultError(event.error);
                observer.next({
                    error: result
                });
            };
        });
    }
    transformResultError(error) {
        const DIFF_TIME_START_NOW = 100;
        switch (error) {
            case 'no-speech':
                this.ignoreOnEnd = true;
                return SpeechError.NoSpeech;
            case 'audio-capture':
                this.ignoreOnEnd = true;
                return SpeechError.NoMicrophone;
            case 'not-allowed':
                this.ignoreOnEnd = true;
                return event.timeStamp - this.startTimestamp < DIFF_TIME_START_NOW
                    ? SpeechError.Blocked
                    : SpeechError.NotAllowed;
            default:
                this.ignoreOnEnd = true;
                return error;
        }
    }
    /**
     * Stop listening and end recognition
     */
    stop() {
        this.recognition.stop();
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: NgSpeechToTextService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: NgSpeechToTextService });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: NgSpeechToTextService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return []; } });

class RecognizerComponent {
    agentService;
    configService;
    speechToTextService;
    conversationService;
    config;
    currentLanguage;
    recognizing;
    destroy$ = new Subject();
    constructor(agentService, configService, speechToTextService, conversationService) {
        this.agentService = agentService;
        this.configService = configService;
        this.speechToTextService = speechToTextService;
        this.conversationService = conversationService;
    }
    ngOnInit() {
        this.config = this.configService.config;
        this.agentService.listenStream()
            .pipe(distinctUntilChanged((prev, curr) => prev.listening === curr.listening), filter(ngAgent => ngAgent.listening), takeUntil(this.destroy$))
            .subscribe(() => {
            this.speechToTextService.initialize();
            this.initRecognition();
            this.start();
        });
    }
    ngOnDestroy() {
        this.destroy$.next(true);
        this.destroy$.unsubscribe();
    }
    /**
     * Start recognizer when recognizing's field its false.
     * If recognizing  it true, stop recognizer.
     * {@link speechRecognizer}
     */
    start() {
        if (this.recognizing) {
            this.speechToTextService.stop();
        }
        else {
            this.speechToTextService.start();
            playAudio(this.config.soundAssistant.listen);
        }
    }
    /**
     * Stop {@link speechRecognizer}
     */
    stop() {
        this.speechToTextService.stop();
    }
    /**
     * Change language by default
     * @param language - string
     */
    onSelectLanguage(language) {
        this.currentLanguage = language;
        this.speechToTextService.setLanguage(this.currentLanguage);
    }
    initRecognition() {
        this.speechToTextService.onStart()
            .pipe(takeUntil(this.destroy$))
            .subscribe(data => this.recognizing = true);
        this.speechToTextService.onEnd()
            .pipe(takeUntil(this.destroy$))
            .subscribe(data => this.recognizing = false);
        this.speechToTextService.onResult()
            .pipe(filter(speech => speech.info === 'final_transcript'), map(speech => speech.content.trim()), filter(message => message.length > 0), takeUntil(this.destroy$))
            .subscribe(message => {
            playAudio(this.config.soundAssistant.endListen);
            this.conversationService.userSaid(message);
            this.agentService.stopListening();
            this.stop();
        });
        this.speechToTextService.onError()
            .pipe(takeUntil(this.destroy$))
            .subscribe(data => {
            this.recognizing = false;
            this.agentService.stopListening();
            console.error(data.error);
        });
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: RecognizerComponent, deps: [{ token: NgxAgentService }, { token: NgxConfigService }, { token: NgSpeechToTextService }, { token: NgxConversationService }], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: RecognizerComponent, selector: "lib-recognizer", ngImport: i0, template: '', isInline: true });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: RecognizerComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'lib-recognizer',
                    template: ''
                }]
        }], ctorParameters: function () { return [{ type: NgxAgentService }, { type: NgxConfigService }, { type: NgSpeechToTextService }, { type: NgxConversationService }]; } });

/**
 * Service for handling text-to-speech using the web SpeechSynthesis API
 */
class NgTextToSpeechService {
    synthesis;
    utteranceFinished = false;
    constructor() {
        this.synthesis = window.speechSynthesis;
    }
    speak(text, language = 'es-ES') {
        const utterance = this.initSynthesis(text, language);
        console.log(`1.${text}\n-this.synthesis.pending: ${this.synthesis.pending}\n-this.synthesis.speaking: ${this.synthesis.speaking}\n-this.synthesis.: ${this.synthesis.paused}`);
        utterance.onerror = event => {
            console.error(`An error has occurred with the speech synthesis: ${event.error}`);
        };
        utterance.onend = event => {
            console.log(`3.${text}\n-this.synthesis.pending: ${this.synthesis.pending}\n-this.synthesis.speaking: ${this.synthesis.speaking}\n-this.synthesis.: ${this.synthesis.paused}`);
            this.utteranceFinished = true;
        };
        this.synthesis.speak(utterance);
        console.log(`2.${text}\n-this.synthesis.pending: ${this.synthesis.pending}\n-this.synthesis.speaking: ${this.synthesis.speaking}\n-this.synthesis.: ${this.synthesis.paused}`);
    }
    onEnd(utterance, text) {
        return new Promise(resolve => utterance.onend = event => {
            console.log(`4.${text}\n-this.synthesis.pending: ${this.synthesis.pending}\n-this.synthesis.speaking: ${this.synthesis.speaking}\n-this.synthesis.: ${this.synthesis.paused}`);
            if (this.synthesis.speaking) {
                this.synthesis.pause();
            }
            if (!this.synthesis.pending) {
                this.synthesis.cancel();
            }
            console.log(`5.${text}\n-this.synthesis.pending: ${this.synthesis.pending}\n-this.synthesis.speaking: ${this.synthesis.speaking}\n-this.synthesis.: ${this.synthesis.paused}`);
            resolve(event);
        });
    }
    initSynthesis(text, language) {
        const utterance = new SpeechSynthesisUtterance(text);
        utterance.lang = language;
        return utterance;
    }
    cancel() {
        this.synthesis.cancel();
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: NgTextToSpeechService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: NgTextToSpeechService });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: NgTextToSpeechService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return []; } });

class SpeakComponent {
    textToSpeechService;
    agentService;
    destroy$ = new Subject();
    constructor(textToSpeechService, agentService) {
        this.textToSpeechService = textToSpeechService;
        this.agentService = agentService;
    }
    ngOnInit() {
        this.agentService.speakStream()
            .pipe(delay(1000), takeUntil(this.destroy$))
            .subscribe(ngAgent => {
            ngAgent.speaking
                ? this.speak(ngAgent.text)
                : this.textToSpeechService.cancel();
        });
    }
    speak(message) {
        console.log(`Speak method into speakComponent before speak. Message: ${message}`);
        this.textToSpeechService.speak(message);
    }
    ngOnDestroy() {
        this.destroy$.next(true);
        this.destroy$.unsubscribe();
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: SpeakComponent, deps: [{ token: NgTextToSpeechService }, { token: NgxAgentService }], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: SpeakComponent, selector: "lib-speak", ngImport: i0, template: '', isInline: true });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: SpeakComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'lib-speak',
                    template: ''
                }]
        }], ctorParameters: function () { return [{ type: NgTextToSpeechService }, { type: NgxAgentService }]; } });

var IntentError;
(function (IntentError) {
    IntentError["noIntentInResponse"] = "No intent found in response";
})(IntentError || (IntentError = {}));

var ErrorMessage;
(function (ErrorMessage) {
    ErrorMessage[ErrorMessage["gotoService"] = 0] = "gotoService";
})(ErrorMessage || (ErrorMessage = {}));
var HandlerError;
(function (HandlerError) {
    HandlerError["ParamsHandlerResolver"] = "Params required: [command] or [command.intentDisplayName] not found";
    HandlerError["NoIntentMatch"] = "Intent not exist";
})(HandlerError || (HandlerError = {}));
var NavigateError;
(function (NavigateError) {
    NavigateError["userServiceNotInstantiated"] = "userService not instantiated!";
    NavigateError["checkoutsUndefined"] = "userService.checkouts not defined";
    NavigateError["holdsUndefined"] = "userService.holds not defined";
    NavigateError["favoritesUndefined"] = "userService.favorites not defined";
    NavigateError["listsUndefined"] = "userService.lists not defined";
})(NavigateError || (NavigateError = {}));
var CollectionsError;
(function (CollectionsError) {
    CollectionsError["noCollection"] = "No collection found";
    CollectionsError["noCollections"] = "No collections found";
})(CollectionsError || (CollectionsError = {}));
var CheckoutDownloadError;
(function (CheckoutDownloadError) {
    CheckoutDownloadError["noFound"] = "No checkout found in CheckoutDownload";
})(CheckoutDownloadError || (CheckoutDownloadError = {}));
var CheckoutUtilError;
(function (CheckoutUtilError) {
    CheckoutUtilError["noActiveCheckouts"] = "No active checkouts";
    CheckoutUtilError["itemNoCheckout"] = "Item no checkout";
    CheckoutUtilError["checkoutRecordResUndefined"] = "Response checkoutRecord() undefined";
})(CheckoutUtilError || (CheckoutUtilError = {}));
var FavoriteError;
(function (FavoriteError) {
    FavoriteError["itemIsFavorite"] = "Item in favorites";
    FavoriteError["notFound"] = "Favorite not found";
})(FavoriteError || (FavoriteError = {}));
var RecordError;
(function (RecordError) {
    RecordError["recordsRequired"] = "Records data required";
    RecordError["noRecord"] = "No record found";
    RecordError["noRecords"] = "No records found";
    RecordError["noRecordId"] = "No recordId found";
    RecordError["noInputData"] = "No data in inputs array";
})(RecordError || (RecordError = {}));
var CommonServiceError;
(function (CommonServiceError) {
    CommonServiceError["noData"] = "Data is required to find an element by title";
    CommonServiceError["noTitle"] = "Title is required";
    CommonServiceError["noElement"] = "Element not found";
})(CommonServiceError || (CommonServiceError = {}));
var HandlerServiceError;
(function (HandlerServiceError) {
    HandlerServiceError["noIntentFound"] = "No intent found";
})(HandlerServiceError || (HandlerServiceError = {}));
var CommandGuardsError;
(function (CommandGuardsError) {
    CommandGuardsError["noTitleField"] = "The title field was not found in the command";
    CommandGuardsError["noAction"] = "Action is not defined";
})(CommandGuardsError || (CommandGuardsError = {}));

class Util {
    static COLLECTIONS_KEY = 'cached-collections';
    static ALLFIELDS = 'allfields_txt';
    static USER_SERVICE = 'UserServiceOpac';
    static API_SERVICE = 'ApiServiceOpac';
    static LOCALE_SERVICE = 'LocalServiceOpac';
    static CONFIG_SERVICE = 'ConfigServiceOpac';
    static NEW_CHECKOUT_NOTIFICATION = 'messages.checkouts.new';
    static RETURN_CHECKOUT_NOTIFICATION = 'messages.checkouts.return';
    static DOWNLOAD_CHECKOUT_NOTIFICATION = 'messages.checkouts.download';
    static ADD_FAVORITE_NOTIFICATION = 'messages.favorites.add';
    static REMOVE_FAVORITE_NOTIFICATION = 'messages.favorites.remove';
    static slugify(text) {
        const a = 'àáäâãåăæąçćčđďèéěėëêęğǵḧìíïîįłḿǹńňñòóöôœøṕŕřßşśšșťțùúüûǘůűūųẃẍÿýźžż·/_,:;';
        const b = 'aaaaaaaaacccddeeeeeeegghiiiiilmnnnnooooooprrsssssttuuuuuuuuuwxyyzzz------';
        const p = new RegExp(a.split('').join('|'), 'g');
        return text.toLowerCase()
            .replace(/\s+/g, '-')
            .replace(p, c => b.charAt(a.indexOf(c)))
            .replace(/&/g, '-and-')
            .replace(/[^\w\-]+/g, '')
            .replace(/\-\-+/g, '-')
            .replace(/^-+/, '')
            .replace(/-+$/, '');
    }
    static slugifyRecord(record) {
        let toSlug = record.title;
        if (record.subtitle) {
            toSlug += ' ' + record.subtitle;
        }
        return Util.slugify(toSlug) + '-' + record.id;
    }
    static split(str, delimiter, limit, join = ' ') {
        return [str.split(delimiter, limit).toString(), str.split(delimiter).slice(limit).join(join)];
    }
    static getTitle(command) {
        if (!Util.titleGuard(command)) {
            throw new Error(CommandGuardsError.noTitleField);
        }
        return command.parameters.fields.title.stringValue;
    }
    static titleGuard(command) {
        return command
            && command.parameters
            && command.parameters.fields
            && command.parameters.fields.title;
    }
    /**
     * Returns items that are only in the first array relative to the second
     * @param a array
     * @param b array
     */
    static compareArraysByTitleAndId(a, b) {
        const comparer = (otherArray) => (current) => otherArray.filter((other) => other.title === current.title && other.id === current.id).length === 0;
        return a.filter(comparer(b));
    }
    static compareArraysByRecordId(a, b) {
        const comparer = (otherArray) => (current) => otherArray.filter((other) => other.recordId === current.id).length === 0;
        return a.filter(comparer(b));
    }
    /**
     * It merge all arrays passed by parameter and returns result.
     * @param arrays spread operator
     */
    static mergeArrays(...arrays) {
        let jointArray = [];
        arrays.forEach(array => {
            jointArray = [...jointArray, ...array];
        });
        const uniqueArray = jointArray.filter((item, index) => jointArray.indexOf(item) === index);
        return uniqueArray;
    }
}

class NgxDialogFlowHttpService {
    http;
    userService;
    configService;
    config;
    SERVER_URL;
    constructor(http, userService, configService) {
        this.http = http;
        this.userService = userService;
        this.configService = configService;
        this.config = this.configService.config;
        this.SERVER_URL = this.config.urlServer;
    }
    detectIntent(text) {
        const headers = new HttpHeaders({
            'x-api-key': this.config.apiKey
        });
        const sessionId = this.userService.getUserId();
        return this.http.post(`${this.SERVER_URL}`, { text, sessionId }, { headers });
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: NgxDialogFlowHttpService, deps: [{ token: i1.HttpClient }, { token: Util.USER_SERVICE }, { token: NgxConfigService }], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: NgxDialogFlowHttpService, providedIn: 'root' });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: NgxDialogFlowHttpService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: i1.HttpClient }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [Util.USER_SERVICE]
                }] }, { type: NgxConfigService }]; } });

class NgxCommandService {
    command = new Subject();
    constructor() { }
    getCommandStream() {
        return this.command.asObservable();
    }
    pushCommand(command) {
        this.command.next(command);
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: NgxCommandService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: NgxCommandService });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: NgxCommandService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return []; } });

class IntentComponent {
    conversationService;
    dialogFlowHttp;
    tutorialService;
    agentService;
    commandService;
    destroy$ = new Subject();
    queryResult;
    outputContexts;
    DEFAULT_INTENT = 'default';
    ID_DIALOG_CONTEXT = 'id_dialog_context';
    TUTORIAL_FOLLOW_UP = 'defaulttutorial-followup';
    constructor(conversationService, dialogFlowHttp, tutorialService, agentService, commandService) {
        this.conversationService = conversationService;
        this.dialogFlowHttp = dialogFlowHttp;
        this.tutorialService = tutorialService;
        this.agentService = agentService;
        this.commandService = commandService;
    }
    ngOnInit() {
        this.conversationService.getUserStream()
            .pipe(map(ngMessage => ngMessage.text), takeUntil(this.destroy$))
            .subscribe(userSaid => this.detectIntent(userSaid));
    }
    detectIntent(userSaid) {
        this.dialogFlowHttp.detectIntent(userSaid)
            .subscribe(res => this.handleDetectIntentResponse(res), error => {
            throw new Error(error);
        });
    }
    handleDetectIntentResponse(response) {
        this.queryResult = response.queryResult;
        this.agentService.speaking(this.queryResult.fulfillmentText);
        this.setOutputContexts(this.queryResult);
        this.detectOutputContextTutorialFollowUp();
        this.detectIntentToSendCommand();
    }
    setOutputContexts(queryResult) {
        if (queryResult.outputContexts && queryResult.outputContexts.length) {
            this.outputContexts = queryResult.outputContexts.map(o => o.name.toLowerCase());
        }
    }
    detectOutputContextTutorialFollowUp() {
        if (this.findOutputContext(this.TUTORIAL_FOLLOW_UP)) {
            this.tutorialService.tutorialIncompleteAction(this.queryResult.action);
            this.agentService.detecting(false);
        }
    }
    detectIntentToSendCommand() {
        const isNotOutputContextIdDialog = !this.findOutputContext(this.ID_DIALOG_CONTEXT);
        if (isNotOutputContextIdDialog && this.notDefaultIntent()) {
            this.commandService.pushCommand(this.buildCommand(this.queryResult));
            this.agentService.detecting(false);
        }
    }
    findOutputContext(str) {
        if (this.outputContexts && this.outputContexts.length) {
            return this.outputContexts.find(o => o.includes(str));
        }
    }
    notDefaultIntent() {
        if (!this.intentGuard) {
            throw new Error(IntentError.noIntentInResponse);
        }
        return !this.queryResult.intent.displayName.toLowerCase().includes(this.DEFAULT_INTENT);
    }
    intentGuard() {
        return this.queryResult
            && this.queryResult.intent
            && this.queryResult.intent.displayName;
    }
    buildCommand(queryResult) {
        const command = {
            intentDisplayName: queryResult.intent.displayName,
            intentDetectionConfidence: queryResult.intentDetectionConfidence,
            action: queryResult.action,
            parameters: queryResult.parameters,
            allRequiredParamsPresent: queryResult.allRequiredParamsPresent,
            fulfillMentText: queryResult.fulfillmentText
        };
        console.log(`PushCommand: ${JSON.stringify(command)}`);
        return command;
    }
    ngOnDestroy() {
        this.destroy$.next(true);
        this.destroy$.unsubscribe();
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: IntentComponent, deps: [{ token: NgxConversationService }, { token: NgxDialogFlowHttpService }, { token: NgxTutorialService }, { token: NgxAgentService }, { token: NgxCommandService }], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: IntentComponent, selector: "lib-intent", ngImport: i0, template: '', isInline: true });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: IntentComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'lib-intent',
                    template: ''
                }]
        }], ctorParameters: function () { return [{ type: NgxConversationService }, { type: NgxDialogFlowHttpService }, { type: NgxTutorialService }, { type: NgxAgentService }, { type: NgxCommandService }]; } });

class ConversationComponent {
    agentService;
    tutorialService;
    microphoneService;
    conversationService;
    notificationService;
    microphone;
    detectingIntent;
    TUTORIAL_START = 'tutorial.start';
    destroy$ = new Subject();
    constructor(agentService, tutorialService, microphoneService, conversationService, notificationService) {
        this.agentService = agentService;
        this.tutorialService = tutorialService;
        this.microphoneService = microphoneService;
        this.conversationService = conversationService;
        this.notificationService = notificationService;
    }
    ngOnInit() {
        // this.tutorialService.getTutorialStream()
        //     .pipe(
        //         filter(tutorial => tutorial.start && tutorial.start === true),
        //         takeUntil(this.destroy$)
        //     )
        //     .subscribe(start => this.tutorial());
        this.agentService.detecIntentStream()
            .pipe(takeUntil(this.destroy$))
            .subscribe(detectingIntent => this.detectingIntent = detectingIntent);
        this.notificationService.getNotificationStream()
            .pipe(delay(500), takeUntil(this.destroy$))
            .subscribe(notification => this.agentService.speaking(notification.content));
    }
    tutorial() {
        this.tutorialService.getTutorialStream()
            .pipe(filter(tutorial => tutorial.action && tutorial.action === this.TUTORIAL_START), takeUntil(this.destroy$))
            .subscribe(tutorial => this.checkMicrophone());
        this.sendMessageToIntent('iniciar tutorial');
    }
    checkMicrophone() {
        window.navigator.mediaDevices.getUserMedia({ audio: true })
            .then(stream => {
            this.microphoneService.activateMicrophone(true);
            this.tutorialService.tutorialCompleted(true);
            this.sendMessageToIntent('mic.on');
        })
            .catch(error => {
            console.error(error);
            this.sendMessageToIntent('mic.off');
        });
    }
    async sendMessageToIntent(action) {
        this.agentService.detecting(true);
        await sleep(TWO_SECOND);
        this.conversationService.userSaid(action);
    }
    ngOnDestroy() {
        this.destroy$.next(true);
        this.destroy$.unsubscribe();
    }
    listenUser() {
        this.agentService.stopSpeaking();
        this.agentService.detecting(true);
        this.agentService.listening();
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: ConversationComponent, deps: [{ token: NgxAgentService }, { token: NgxTutorialService }, { token: NgxMicrophoneService }, { token: NgxConversationService }, { token: NgxNotificationService }], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: ConversationComponent, selector: "lib-conversation", host: { listeners: { "document:keydown.shift.s": "listenUser()", "document:keydown.shift.control.s": "listenUser()", "document:keydown.alt.s": "listenUser()", "document:keydown.control.alt.s": "listenUser()" } }, ngImport: i0, template: "<lib-speak></lib-speak>\n<lib-recognizer></lib-recognizer>\n<lib-intent *ngIf=\"detectingIntent\"></lib-intent>", dependencies: [{ kind: "directive", type: i6.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: RecognizerComponent, selector: "lib-recognizer" }, { kind: "component", type: SpeakComponent, selector: "lib-speak" }, { kind: "component", type: IntentComponent, selector: "lib-intent" }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: ConversationComponent, decorators: [{
            type: Component,
            args: [{ selector: 'lib-conversation', template: "<lib-speak></lib-speak>\n<lib-recognizer></lib-recognizer>\n<lib-intent *ngIf=\"detectingIntent\"></lib-intent>" }]
        }], ctorParameters: function () { return [{ type: NgxAgentService }, { type: NgxTutorialService }, { type: NgxMicrophoneService }, { type: NgxConversationService }, { type: NgxNotificationService }]; }, propDecorators: { listenUser: [{
                type: HostListener,
                args: ['document:keydown.shift.s']
            }, {
                type: HostListener,
                args: ['document:keydown.shift.control.s']
            }, {
                type: HostListener,
                args: ['document:keydown.alt.s']
            }, {
                type: HostListener,
                args: ['document:keydown.control.alt.s']
            }] } });

class NgAssistantCoreModule {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: NgAssistantCoreModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.2.12", ngImport: i0, type: NgAssistantCoreModule });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: NgAssistantCoreModule, providers: [NgxConversationService] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: NgAssistantCoreModule, decorators: [{
            type: NgModule,
            args: [{
                    providers: [NgxConversationService]
                }]
        }] });

class NgAssistantWebModule {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: NgAssistantWebModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.2.12", ngImport: i0, type: NgAssistantWebModule, imports: [NgAssistantCoreModule], exports: [NgAssistantCoreModule] });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: NgAssistantWebModule, providers: [NgSpeechToTextService, NgTextToSpeechService], imports: [NgAssistantCoreModule, NgAssistantCoreModule] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: NgAssistantWebModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        NgAssistantCoreModule
                    ],
                    providers: [NgSpeechToTextService, NgTextToSpeechService],
                    exports: [NgAssistantCoreModule]
                }]
        }] });

const SPEECH_API_RECOGNITION = ['webkitSpeechRecognition', 'SpeechRecognition'];
const SPEECH_API_SYNTHESIS = ['speechSynthesis'];
function isWebSpeechSupported(window) {
    return checkPropertiesInObject(SPEECH_API_RECOGNITION, window) && checkPropertiesInObject(SPEECH_API_SYNTHESIS, window);
}

class NgxAssistantService {
    assistantSubject = new Subject();
    constructor() { }
    /**
     * To Listen notifications about change state from asssitant
     * @returns A Observable<NgAssistant> that take notifications about change state from assistant
     */
    getAssistantStream() {
        return this.assistantSubject.asObservable();
    }
    /**
     * Send active notification to subscribers assistantState. For example {@link WebSpeechComponent}
     * Use active field of {@link NgAssistant} to control state with true or false
     * @param info By default "Active"
     */
    activateAssistant(info = 'Active') {
        this.assistantSubject.next({
            active: true,
            info
        });
    }
    /**
     * Send desactive notification to subscribers assistantState
     * Use active field of {@link NgAssistant} to control state with true or false
     * @param info By default "Desactivate"
     */
    desactivateAssistant(info = 'Desactivate') {
        this.assistantSubject.next({
            active: false,
            info
        });
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: NgxAssistantService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: NgxAssistantService });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: NgxAssistantService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return []; } });

class NgxAssistantComponent {
    assistantService;
    microphoneService;
    agentService;
    tutorialService;
    configService;
    browserSupport;
    assistantActive;
    userLogged;
    tutorialComplete;
    config;
    destroy$;
    microphone$;
    assistant$;
    assistantSubs$;
    microphoneAndAssistantAreActive$;
    constructor(assistantService, microphoneService, agentService, tutorialService, configService) {
        this.assistantService = assistantService;
        this.microphoneService = microphoneService;
        this.agentService = agentService;
        this.tutorialService = tutorialService;
        this.configService = configService;
        this.destroy$ = new Subject();
        this.microphone$ = microphoneService.getMicrophoneStream();
        this.assistant$ = assistantService.getAssistantStream();
        this.assistantSubs$ = this.assistant$
            .pipe(takeUntil(this.destroy$))
            .subscribe((assistant) => (this.assistantActive = assistant.active));
        this.microphoneAndAssistantAreActive$ = combineLatest([
            this.assistant$,
            this.microphone$,
        ]).pipe(map(([assistant, microphone]) => assistant.active && microphone.active));
    }
    ngOnInit() {
        this.tutorialService
            .getTutorialStream()
            .pipe(filter((tutorial) => tutorial.complete === true), takeUntil(this.destroy$))
            .subscribe((tutorial) => (this.tutorialComplete = tutorial.complete));
        this.config = this.configService.config;
        if (this.config.tutorialActive && !this.tutorialComplete) {
            this.tutorialService.startTutorial(true);
        }
        this.browserSupport = isWebSpeechSupported(window);
    }
    ngOnDestroy() {
        this.destroy$.next(true);
        this.destroy$.unsubscribe();
    }
    /**
     * Activate assistant with shortcut:
     * "shift + k"
     * "shift + alt + k"
     * "shift + meta + k"
     * "control + meta + k"
     * "control + alt + k"
     * The {@link AssistantService} takes care of sending the state to the subscribers
     */
    startAssistant() {
        if (!this.assistantActive) {
            playAudio(this.config.soundAssistant.start);
            this.assistantService.activateAssistant();
        }
    }
    /**
     * Desactivate assistant with shortcuts:
     * "shift + i"
     * "shift + alt + i"
     * "shift + meta + i"
     * "control + meta + i"
     * "control + alt + i"
     * The {@link AssistantService} takes care of sending the state to the subscribers
     */
    stopAssistant() {
        if (this.assistantActive) {
            playAudio(this.config.soundAssistant.end);
            this.agentService.stopSpeaking();
            this.assistantService.desactivateAssistant();
        }
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: NgxAssistantComponent, deps: [{ token: NgxAssistantService }, { token: NgxMicrophoneService }, { token: NgxAgentService }, { token: NgxTutorialService }, { token: NgxConfigService }], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: NgxAssistantComponent, selector: "lib-ngx-assistant", host: { listeners: { "document:keydown.shift.k": "startAssistant()", "document:keydown.shift.alt.k": "startAssistant()", "document:keydown.shift.meta.k": "startAssistant()", "document:keydown.control.meta.k": "startAssistant()", "document:keydown.control.alt.k": "startAssistant()", "document:keydown.shift.i": "stopAssistant()", "document:keydown.meta.control.i": "stopAssistant()", "document:keydown.control.alt.i": "stopAssistant()", "document:keydown.shift.alt.i": "stopAssistant()", "document:keydown.shift.meta.i": "stopAssistant()" } }, providers: [
            NgxAssistantService,
            NgxConversationService,
            NgxMicrophoneService,
            NgxAgentService,
            NgxTutorialService,
        ], ngImport: i0, template: "<div *ngIf=\"(microphoneAndAssistantAreActive$ | async); else assistantDesactivate\">\n  <div *ngIf=\"browserSupport; else notBrowserSupport\">\n    <lib-conversation></lib-conversation>\n  </div>\n  <ng-template #notBrowserSupport>\n    <!-- <mat-chip-list aria-label=\"browser not support\">\n      <mat-chip color=\"accent\" selected>Tu navegador no est\u00E1 soportado</mat-chip>\n    </mat-chip-list> -->\n  </ng-template>\n</div>\n<ng-template #assistantDesactivate>\n  <!-- <mat-chip-list aria-label=\"assistant desactivate\">\n    <mat-chip color=\"accent\" selected>Asistente desactivado</mat-chip>\n  </mat-chip-list> -->\n</ng-template>", dependencies: [{ kind: "directive", type: i6.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: ConversationComponent, selector: "lib-conversation" }, { kind: "pipe", type: i6.AsyncPipe, name: "async" }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: NgxAssistantComponent, decorators: [{
            type: Component,
            args: [{ selector: "lib-ngx-assistant", providers: [
                        NgxAssistantService,
                        NgxConversationService,
                        NgxMicrophoneService,
                        NgxAgentService,
                        NgxTutorialService,
                    ], template: "<div *ngIf=\"(microphoneAndAssistantAreActive$ | async); else assistantDesactivate\">\n  <div *ngIf=\"browserSupport; else notBrowserSupport\">\n    <lib-conversation></lib-conversation>\n  </div>\n  <ng-template #notBrowserSupport>\n    <!-- <mat-chip-list aria-label=\"browser not support\">\n      <mat-chip color=\"accent\" selected>Tu navegador no est\u00E1 soportado</mat-chip>\n    </mat-chip-list> -->\n  </ng-template>\n</div>\n<ng-template #assistantDesactivate>\n  <!-- <mat-chip-list aria-label=\"assistant desactivate\">\n    <mat-chip color=\"accent\" selected>Asistente desactivado</mat-chip>\n  </mat-chip-list> -->\n</ng-template>" }]
        }], ctorParameters: function () { return [{ type: NgxAssistantService }, { type: NgxMicrophoneService }, { type: NgxAgentService }, { type: NgxTutorialService }, { type: NgxConfigService }]; }, propDecorators: { startAssistant: [{
                type: HostListener,
                args: ["document:keydown.shift.k"]
            }, {
                type: HostListener,
                args: ["document:keydown.shift.alt.k"]
            }, {
                type: HostListener,
                args: ["document:keydown.shift.meta.k"]
            }, {
                type: HostListener,
                args: ["document:keydown.control.meta.k"]
            }, {
                type: HostListener,
                args: ["document:keydown.control.alt.k"]
            }], stopAssistant: [{
                type: HostListener,
                args: ["document:keydown.shift.i"]
            }, {
                type: HostListener,
                args: ["document:keydown.meta.control.i"]
            }, {
                type: HostListener,
                args: ["document:keydown.control.alt.i"]
            }, {
                type: HostListener,
                args: ["document:keydown.shift.alt.i"]
            }, {
                type: HostListener,
                args: ["document:keydown.shift.meta.i"]
            }] } });

class NgxErrorService {
    error$ = new Subject();
    constructor() { }
    getErrorStream() {
        return this.error$.asObservable();
    }
    push(error) {
        this.error$.next(error);
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: NgxErrorService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: NgxErrorService });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: NgxErrorService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return []; } });

class NgxAssistantModule {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: NgxAssistantModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.2.12", ngImport: i0, type: NgxAssistantModule, declarations: [NgxAssistantComponent,
            RecognizerComponent,
            SpeakComponent,
            IntentComponent,
            ConversationComponent], imports: [CommonModule,
            FormsModule,
            NgAssistantWebModule], exports: [NgxAssistantComponent] });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: NgxAssistantModule, providers: [NgxCommandService, NgxNotificationService, NgxErrorService], imports: [CommonModule,
            FormsModule,
            NgAssistantWebModule] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: NgxAssistantModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        FormsModule,
                        NgAssistantWebModule
                    ],
                    declarations: [
                        NgxAssistantComponent,
                        RecognizerComponent,
                        SpeakComponent,
                        IntentComponent,
                        ConversationComponent
                    ],
                    providers: [NgxCommandService, NgxNotificationService, NgxErrorService],
                    exports: [NgxAssistantComponent]
                }]
        }] });

class CollectionsUtilService {
    constructor() { }
    getCollections() {
        try {
            return JSON.parse(sessionStorage.getItem(Util.COLLECTIONS_KEY));
        }
        catch (e) {
            console.error(e);
            sessionStorage.removeItem(Util.COLLECTIONS_KEY);
        }
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: CollectionsUtilService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: CollectionsUtilService });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: CollectionsUtilService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return []; } });

var Path;
(function (Path) {
    Path["home"] = "/";
    Path["checkouts"] = "/user/checkouts";
    Path["checkoutsHistory"] = "/user/checkouts/history";
    Path["holds"] = "/user/holds";
    Path["favorites"] = "/user/favorites";
    Path["lists"] = "/user/lists";
    Path["purchaseSuggestions"] = "/user/suggestions";
    Path["visualizationHistory"] = "/user/visualizations";
    Path["savedSearches"] = "/user/search";
    Path["stats"] = "/user/stats";
    Path["devices"] = "/user/devices";
    Path["account"] = "/user/details";
    Path["notifications"] = "/user/notifications";
})(Path || (Path = {}));
var Name;
(function (Name) {
    Name["home"] = "pagina principal";
    Name["checkouts"] = "prestamos";
    Name["holds"] = "reservas";
    Name["favorites"] = "favoritos";
    Name["lists"] = "listas";
})(Name || (Name = {}));

class CommonService {
    localeService;
    notificationService;
    zone;
    router;
    CLEANER = /[\u0300-\u036f]|\:|\,|\"|'/g;
    constructor(localeService, notificationService, zone, router) {
        this.localeService = localeService;
        this.notificationService = notificationService;
        this.zone = zone;
        this.router = router;
    }
    compareTitles(a, b) {
        const sanetize = (str) => str.toLowerCase().normalize('NFD').replace(this.CLEANER, '');
        return sanetize(a) === sanetize(b);
    }
    findElementByTitle(title, data) {
        if (!data && !data.length) {
            throw new Error(CommonServiceError.noData);
        }
        if (!title) {
            throw new Error(CommonServiceError.noTitle);
        }
        const element = data.find(r => this.compareTitles(r.title, title));
        if (!element) {
            throw new Error(CommonServiceError.noElement);
        }
        return element;
    }
    navigate(url, success) {
        this.zone.run(() => {
            this.router.navigate([url])
                .then(bool => {
                if (!bool) {
                    this.router.navigate([Path.home]);
                    return;
                }
                if (success) {
                    success();
                }
            }).catch(console.error);
        });
    }
    manageSuccesfulAction(actionNotification) {
        this.pushInfoNotification(actionNotification.phrase);
        this.localeService.notify(actionNotification.notification);
        this.navigate(actionNotification.path);
    }
    pushErrorNotification(error) {
        if (!error) {
            throw new Error("Parameter error required");
        }
        this.notificationService.pushNotification({
            content: error
        });
    }
    pushInfoNotification(info) {
        if (!info) {
            throw new Error("Parameter info required");
        }
        this.notificationService.pushNotification({
            content: info
        });
    }
    pushResultsNotification(results) {
        results.forEach(result => this.notificationService.pushNotification({
            content: result
        }));
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: CommonService, deps: [{ token: Util.LOCALE_SERVICE }, { token: NgxNotificationService }, { token: i0.NgZone }, { token: i1$1.Router }], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: CommonService });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: CommonService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [Util.LOCALE_SERVICE]
                }] }, { type: NgxNotificationService }, { type: i0.NgZone }, { type: i1$1.Router }]; } });

class RecordUtilService {
    userService;
    apiService;
    localeService;
    collectionUtil;
    commonService;
    records = [];
    constructor(userService, apiService, localeService, collectionUtil, commonService) {
        this.userService = userService;
        this.apiService = apiService;
        this.localeService = localeService;
        this.collectionUtil = collectionUtil;
        this.commonService = commonService;
    }
    getRecords() {
        return this.records;
    }
    getRecord(title) {
        return this.commonService.findElementByTitle(title, this.records);
    }
    getRecordById(recordId) {
        return this.records.find(r => r.id === recordId);
    }
    pushRecords(rec) {
        if (!rec.length) {
            throw new Error(RecordError.recordsRequired);
        }
        const distinctRecords = Util.compareArraysByTitleAndId(rec, this.records);
        if (distinctRecords && distinctRecords.length) {
            this.records = Util.mergeArrays(this.records, distinctRecords);
        }
    }
    /**
     * Load data passed by parameter to records (local variable record-util.service)
     * @param input Array of data to be loaded into records. If it is empty, an error is triggered
     */
    loadDataInRecords(input) {
        if (!input.length) {
            throw new Error(RecordError.noInputData);
        }
        const checkoutsNotInRecords = Util.compareArraysByRecordId(input, this.records);
        checkoutsNotInRecords.forEach(c => this.elementToRecord(c)
            .then(record => this.records.push(record))
            .catch(console.error));
    }
    /**
     * Convert checkout to record via call to api.service
     * @param e Element to convert
     */
    elementToRecord(e) {
        if (!e || !e.recordId) {
            throw new Error(`${RecordError.noRecordId} => e: ${JSON.stringify(e, undefined, 2)}`);
        }
        return new Promise((resolve, reject) => {
            this.apiService.getRecord(e.recordId)
                .subscribe((record) => resolve(record), (error) => reject(error));
        });
    }
    loadCollections() {
        this.getCollections().then(collections => {
            const records = collections.map(collection => collection.records);
            const recordsFromCollecions = records.concat.apply([], records);
            this.pushRecords(recordsFromCollecions);
        });
    }
    async getCollections() {
        const collections = this.collectionUtil.getCollections();
        if (!collections) {
            try {
                const collectionsFromApi = await this.getCollectionsApiService();
                return collectionsFromApi;
            }
            catch (error) {
                throw new Error(`${CollectionsError.noCollections}: [${error.message}]`);
            }
        }
        return collections;
    }
    getCollectionsApiService() {
        return new Promise((resolve, reject) => {
            this.apiService.getCollections(this.localeService.language)
                .subscribe((collections) => resolve(collections), (error) => reject(error));
        });
    }
    isAvailability(recordId) {
        return new Promise((resolve, reject) => {
            this.apiService.getRecordAvailability(recordId, this.userService.getUserId())
                .subscribe((res) => {
                if (res.availableCopies > 0) {
                    resolve(true);
                }
                resolve(false);
            }, err => reject(err));
        });
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: RecordUtilService, deps: [{ token: Util.USER_SERVICE }, { token: Util.API_SERVICE }, { token: Util.LOCALE_SERVICE }, { token: CollectionsUtilService }, { token: CommonService }], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: RecordUtilService });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: RecordUtilService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [Util.USER_SERVICE]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [Util.API_SERVICE]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [Util.LOCALE_SERVICE]
                }] }, { type: CollectionsUtilService }, { type: CommonService }]; } });

class NgxSyncDataService {
    config = new Subject();
    config$ = this.config.asObservable();
    userData = new Subject();
    userData$ = this.userData.asObservable();
    constructor() { }
    sendConfig(config) {
        this.config.next(config);
    }
    sendUserData(data) {
        this.userData.next(data);
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: NgxSyncDataService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: NgxSyncDataService, providedIn: 'root' });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: NgxSyncDataService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return []; } });

var NavigatePhrases;
(function (NavigatePhrases) {
    NavigatePhrases["fallback"] = "Disculpa pero ya est\u00E1s en ese lugar...";
    NavigatePhrases["readCheckouts"] = "Tus prestamos son...";
    NavigatePhrases["noCheckouts"] = "Parece que no tienes prestamos en activo en estos momentos...";
    NavigatePhrases["readHolds"] = "Tus reservas son...";
    NavigatePhrases["noHolds"] = "En estos momentos no tienes reservas...";
    NavigatePhrases["readFavorites"] = "Tienes como favoritos los siguientes t\u00EDtulos...";
    NavigatePhrases["noFavorites"] = "Parece que no tienes ning\u00FAn favorito en estos momentos";
    NavigatePhrases["readLists"] = "Tienes la siguientes listas:";
    NavigatePhrases["noLists"] = "En este momento no veo que tengas ninguna lista activa";
})(NavigatePhrases || (NavigatePhrases = {}));
var CollectionsPhrases;
(function (CollectionsPhrases) {
    CollectionsPhrases["noCollection"] = "Esa colecci\u00F3n no est\u00E1 disponible";
    CollectionsPhrases["noCollections"] = "No se ha encontrado ninguna colecci\u00F3n";
})(CollectionsPhrases || (CollectionsPhrases = {}));
var CheckoutDownloadPhrases;
(function (CheckoutDownloadPhrases) {
    CheckoutDownloadPhrases["noFound"] = "No he encontrado el art\u00EDculo con ese t\u00EDtulo entre tus pr\u00E9stamos";
})(CheckoutDownloadPhrases || (CheckoutDownloadPhrases = {}));
var CheckoutGenericPhrases;
(function (CheckoutGenericPhrases) {
    CheckoutGenericPhrases["itemCheckout"] = "Tienes este art\u00EDculo prestado";
})(CheckoutGenericPhrases || (CheckoutGenericPhrases = {}));
var CheckoutUtilPhrases;
(function (CheckoutUtilPhrases) {
    CheckoutUtilPhrases["noActiveCheckouts"] = "No tienes ning\u00FAn pr\u00E9stamo en activo";
    CheckoutUtilPhrases["itemNoCheckout"] = "No tienes prestado ese art\u00EDculo";
    CheckoutUtilPhrases["itemInCheckout"] = "Ese art\u00EDculo ya est\u00E1 entre tus pr\u00E9stamos";
    CheckoutUtilPhrases["recordCheckout"] = "Pr\u00E9stamo realizado satisfactoriamente";
    CheckoutUtilPhrases["recordCheckoutFail"] = "Parece que ha habido alg\u00FAn problema mientras procesabamos tu pr\u00E9stamo";
    CheckoutUtilPhrases["returnCheckout"] = "Pr\u00E9stamo devuelto";
    CheckoutUtilPhrases["returnCheckoutFail"] = "Parece que ha habido alg\u00FAn problema mientras devolv\u00EDamos tu pr\u00E9stamo";
})(CheckoutUtilPhrases || (CheckoutUtilPhrases = {}));
var FavoritesPhrases;
(function (FavoritesPhrases) {
    FavoritesPhrases["added"] = "Art\u00EDculo a\u00F1adido a favoritos";
    FavoritesPhrases["removed"] = "Art\u00EDculo eliminado de favoritos";
    FavoritesPhrases["itemIsFavorite"] = "Este art\u00EDculo est\u00E1 entre tus favoritos";
    FavoritesPhrases["notFound"] = "Ese art\u00EDculo no est\u00E1 entre tus favoritos";
})(FavoritesPhrases || (FavoritesPhrases = {}));
var HoldsPhrases;
(function (HoldsPhrases) {
    HoldsPhrases["reservated"] = "Art\u00EDculo reservado";
})(HoldsPhrases || (HoldsPhrases = {}));
var SearchPhrases;
(function (SearchPhrases) {
    SearchPhrases["noPreviouSearch"] = "No hay una busqueda previa";
    SearchPhrases["noResultsPrevious"] = "No hay resultados previos";
    SearchPhrases["noMoreResults"] = "No hay m\u00E1s resultados";
    SearchPhrases["noResults"] = "No hay resultados para esta busqueda";
})(SearchPhrases || (SearchPhrases = {}));
var RecordsPhrases;
(function (RecordsPhrases) {
    RecordsPhrases["noRecordFound"] = "No se ha encontrado ning\u00FAn registro con ese t\u00EDtulo";
    RecordsPhrases["noRecordsFound"] = "No hay registros actualmente";
})(RecordsPhrases || (RecordsPhrases = {}));
var GenericPhrases;
(function (GenericPhrases) {
    GenericPhrases["sorryTryAgain"] = "Por favor, vuelve a intentarlo m\u00E1s tarde. Disculpa las molestias";
    GenericPhrases["noResult"] = "No he logrado encontrar ning\u00FAn resultado";
    GenericPhrases["checkAvailability"] = "Comprobando disponibilidad...";
    GenericPhrases["availabilityItem"] = "Art\u00EDculo disponible para tomar en pr\u00E9stamo";
    GenericPhrases["noAvailability"] = "Art\u00EDculo no disponible. Puedes reservarlo para tomar en pr\u00E9stamo cuando est\u00E9 disponible";
    GenericPhrases["misunderstand"] = "Disculpa pero no te he entendido";
})(GenericPhrases || (GenericPhrases = {}));

var LocationTerm;
(function (LocationTerm) {
    LocationTerm["home"] = "home";
    LocationTerm["account"] = "account";
    LocationTerm["checkouts"] = "checkouts";
    LocationTerm["checkoutsHistory"] = "checkoutHistory";
    LocationTerm["holds"] = "holds";
    LocationTerm["purchaseSuggestions"] = "purchaseSuggestions";
    LocationTerm["paginator"] = "paginator";
    LocationTerm["bookClub"] = "club";
    LocationTerm["lists"] = "lists";
    LocationTerm["savedSearches"] = "savedSearches";
    LocationTerm["favorites"] = "favorites";
    LocationTerm["stats"] = "stats";
    LocationTerm["devices"] = "devices";
    LocationTerm["visualizationHistory"] = "visualizationHistory";
    LocationTerm["notifications"] = "notifications";
    LocationTerm["desiderata"] = "desiderata";
    LocationTerm["previous"] = "previous";
})(LocationTerm || (LocationTerm = {}));

class HomePage {
    path;
    name;
    constructor(path = Path.home, name = Name.home) {
        this.path = path;
        this.name = name;
    }
    static create(path, name) {
        return new HomePage(path, name);
    }
}

class CheckoutPage {
    path;
    name;
    userService;
    constructor(userService, path, name) {
        this.userService = userService;
        this.path = path;
        this.name = name;
    }
    static create(userService, path = Path.checkouts, name = Name.checkouts) {
        if (!userService) {
            throw new Error(NavigateError.userServiceNotInstantiated);
        }
        return new CheckoutPage(userService, path, name);
    }
    getCustomMessages() {
        return {
            info: NavigatePhrases.readCheckouts,
            error: NavigatePhrases.noCheckouts
        };
    }
    getResult() {
        if (this.userService.checkouts === undefined) {
            throw new Error(NavigateError.checkoutsUndefined);
        }
        return this.userService.checkouts.map((checkout) => checkout.title);
    }
}

class HoldsPage {
    path;
    name;
    userService;
    constructor(userService, path, name) {
        this.userService = userService;
        this.path = path;
        this.name = name;
    }
    static create(userService, path = Path.holds, name = Name.holds) {
        if (!userService) {
            throw new Error(NavigateError.userServiceNotInstantiated);
        }
        return new HoldsPage(userService, path, name);
    }
    getCustomMessages() {
        return {
            info: NavigatePhrases.readHolds,
            error: NavigatePhrases.noHolds
        };
    }
    getResult() {
        if (this.userService.holds === undefined) {
            throw new Error(NavigateError.holdsUndefined);
        }
        return this.userService.holds.map(hold => hold.title);
    }
}

class FavoritesPage {
    path;
    name;
    userService;
    constructor(userService, path, name) {
        this.userService = userService;
        this.path = path;
        this.name = name;
    }
    static create(userService, path = Path.favorites, name = Name.favorites) {
        if (!userService) {
            throw new Error(NavigateError.checkoutsUndefined);
        }
        return new FavoritesPage(userService, path, name);
    }
    getCustomMessages() {
        return {
            info: NavigatePhrases.readFavorites,
            error: NavigatePhrases.noFavorites
        };
    }
    getResult() {
        if (this.userService.favorites === undefined) {
            throw new Error(NavigateError.favoritesUndefined);
        }
        return this.userService.favorites.map(fav => fav.title);
    }
}

class ListsPage {
    path;
    name;
    userService;
    constructor(userService, path, name) {
        this.userService = userService;
        this.path = path;
        this.name = name;
    }
    static create(userService, path = Path.lists, name = Name.lists) {
        if (!userService) {
            throw new Error(NavigateError.userServiceNotInstantiated);
        }
        return new ListsPage(userService, path, name);
    }
    getCustomMessages() {
        return {
            error: NavigatePhrases.noLists
        };
    }
    getResult() {
        const results = [];
        if (this.userService.lists === undefined) {
            throw new Error(NavigateError.listsUndefined);
        }
        for (const list of this.userService.lists) {
            results.push(`Tu lista de ${list.name} tiene los siguientes títulos: `);
            list.items.map(item => item.title).forEach(item => results.push(item));
        }
        return results;
    }
}

class PageFactoryService {
    userService;
    constructor(userService) {
        this.userService = userService;
    }
    create(location) {
        switch (location) {
            case LocationTerm.home: return HomePage.create();
            case LocationTerm.checkouts: return CheckoutPage.create(this.userService);
            case LocationTerm.holds: return HoldsPage.create(this.userService);
            case LocationTerm.favorites: return FavoritesPage.create(this.userService);
            case LocationTerm.lists: return ListsPage.create(this.userService);
            default: throw new Error(`${location} is not a valid page`);
        }
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: PageFactoryService, deps: [{ token: Util.USER_SERVICE }], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: PageFactoryService });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: PageFactoryService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [Util.USER_SERVICE]
                }] }]; } });

class NavigateHandler {
    router;
    location;
    pageFactory;
    commonService;
    page;
    results;
    messages;
    locationTerm;
    constructor(router, location, pageFactory, commonService) {
        this.router = router;
        this.location = location;
        this.pageFactory = pageFactory;
        this.commonService = commonService;
    }
    run(command) {
        try {
            this.locationTerm = this.getLocation(command);
            this.page = this.pageFactory.create(this.locationTerm);
        }
        catch (error) {
            if (this.locationTerm && this.locationTerm === LocationTerm.previous) {
                this.location.back();
                return;
            }
            throw error;
        }
        if (this.equalsCurrentPath(this.page.path)) {
            this.commonService.pushInfoNotification(NavigatePhrases.fallback);
            return;
        }
        this.commonService.navigate(this.page.path, this.processNavPromise);
    }
    getLocation(command) {
        return command.parameters.fields.term.stringValue;
    }
    equalsCurrentPath(pagePath) {
        return this.router.url === pagePath;
    }
    processNavPromise = () => {
        if (typeof this.page.getResult !== undefined
            && this.page.getCustomMessages !== undefined) {
            this.loadDataPage();
            this.readResults();
        }
    };
    loadDataPage() {
        this.results = this.page.getResult();
        this.messages = this.page.getCustomMessages();
    }
    readResults() {
        if (!this.results.length) {
            this.commonService.pushErrorNotification(this.messages.error);
            return;
        }
        this.commonService.pushInfoNotification(this.messages.info);
        this.commonService.pushResultsNotification(this.results);
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: NavigateHandler, deps: [{ token: i1$1.Router }, { token: i6.Location }, { token: PageFactoryService }, { token: CommonService }], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: NavigateHandler });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: NavigateHandler, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: i1$1.Router }, { type: i6.Location }, { type: PageFactoryService }, { type: CommonService }]; } });

class CheckoutUtilService {
    userService;
    apiService;
    localeService;
    router;
    recordUtil;
    commonService;
    constructor(userService, apiService, localeService, router, recordUtil, commonService) {
        this.userService = userService;
        this.apiService = apiService;
        this.localeService = localeService;
        this.router = router;
        this.recordUtil = recordUtil;
        this.commonService = commonService;
    }
    getCheckouts() {
        if (!this.userService.checkouts.length) {
            this.commonService.pushErrorNotification(CheckoutUtilPhrases.noActiveCheckouts);
            throw new Error(CheckoutUtilError.noActiveCheckouts);
        }
        return this.userService.checkouts;
    }
    getCheckout(recordId) {
        const checkout = this.userService.getCheckoutByRecordId(recordId);
        if (!checkout) {
            this.commonService.pushErrorNotification(CheckoutUtilPhrases.itemNoCheckout);
            throw new Error(CheckoutUtilError.itemNoCheckout);
        }
        return checkout;
    }
    getCheckoutByTitle(title) {
        return this.commonService.findElementByTitle(title, this.getCheckouts());
    }
    getCheckoutById(checkoutId) {
        return this.getCheckouts().find(c => c.id === checkoutId);
    }
    async recordCheckout(recordId, title, userId, fromScreen = 'INIT_SCREEN') {
        if (this.userService.getCheckoutByRecordId(recordId)) {
            throw new Error(`Record: ${recordId} its checkout`);
        }
        if (!title || !userId) {
            throw new Error(`Title: {title} and UserId: {userId} are required`);
        }
        if (await !this.recordUtil.isAvailability(recordId)) {
            throw new Error(`Record: ${recordId} not availability`);
        }
        this.recordCheckout$(recordId, userId, fromScreen, title);
    }
    recordCheckout$(recordId, userId, fromScreen, title) {
        this.apiService.checkoutRecord(recordId, userId, fromScreen)
            .subscribe((checkoutResult) => this.handleResultCheckoutRecord(checkoutResult, title), err => {
            this.commonService.pushErrorNotification(CheckoutUtilPhrases.recordCheckoutFail);
            console.error(err.message);
        });
    }
    handleResultCheckoutRecord(checkoutResult, title) {
        checkoutResult.title = title;
        this.userService.checkouts.unshift(checkoutResult);
        this.commonService.manageSuccesfulAction({
            phrase: CheckoutUtilPhrases.recordCheckout,
            notification: Util.NEW_CHECKOUT_NOTIFICATION,
            path: Path.checkouts
        });
    }
    returnCheckout(userId, checkoutId) {
        const checkout = this.getCheckoutById(checkoutId);
        if (!checkout) {
            throw new Error(`Checkout: ${checkoutId} not found`);
        }
        this.returnCheckout$(userId, checkoutId);
    }
    returnCheckout$(userId, checkoutId) {
        this.apiService.returnCheckout(userId, checkoutId)
            .subscribe((checkout) => this.handleResultReturnCheckout(checkoutId), err => {
            this.commonService.pushErrorNotification(CheckoutUtilPhrases.returnCheckoutFail);
            console.error(err.message);
        });
    }
    handleResultReturnCheckout(checkoutId) {
        this.removeCheckoutById(checkoutId);
        this.commonService.manageSuccesfulAction({
            phrase: CheckoutUtilPhrases.returnCheckout,
            notification: Util.RETURN_CHECKOUT_NOTIFICATION,
            path: Path.checkouts
        });
    }
    removeCheckoutById(idCheckout) {
        const compareId = (checkout) => checkout.id === idCheckout;
        const checkouts = this.userService.checkouts;
        if (!checkouts) {
            throw new Error(`Checkouts from userService not found`);
        }
        const checkoutFound = checkouts.find(compareId);
        if (!checkoutFound) {
            throw new Error(`Checkout: ${compareId} not found`);
        }
        checkouts.splice(checkouts.indexOf(checkoutFound), 1);
    }
    downloadResource(checkoutId, userId, format, token) {
        this.apiService.downloadResource(checkoutId, userId, format, token)
            .subscribe(res => {
            if (typeof res.redirect === 'string') {
                window.open(res.redirect, '_blank');
                if (format.includes('MP3_LH')) {
                    this.localeService.notify(Util.DOWNLOAD_CHECKOUT_NOTIFICATION, 'info');
                }
            }
            else {
                if (res.redirect) {
                    window.open(res.redirect[0], '_blank');
                }
            }
        }, err => {
            this.commonService.pushErrorNotification("No ha sido posible realizar la descarga");
            console.error(err);
        });
    }
    getFormatToDownload(record) {
        let defaultFormat;
        record.formats.forEach((format) => {
            if (!defaultFormat && !['ACSM', 'OCS'].includes(format)) {
                defaultFormat = format;
            }
        });
        return defaultFormat;
    }
    getToken(downloadUrl) {
        const url = downloadUrl.replace(/^(https?:|)\/\//, '');
        const parsedUrl = this.router.parseUrl(url);
        return parsedUrl.queryParams.token;
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: CheckoutUtilService, deps: [{ token: Util.USER_SERVICE }, { token: Util.API_SERVICE }, { token: Util.LOCALE_SERVICE }, { token: i1$1.Router }, { token: RecordUtilService }, { token: CommonService }], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: CheckoutUtilService });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: CheckoutUtilService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [Util.USER_SERVICE]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [Util.API_SERVICE]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [Util.LOCALE_SERVICE]
                }] }, { type: i1$1.Router }, { type: RecordUtilService }, { type: CommonService }]; } });

class CheckoutReturnService {
    userService;
    checkoutUtil;
    commonService;
    checkoutMatched;
    constructor(userService, checkoutUtil, commonService) {
        this.userService = userService;
        this.checkoutUtil = checkoutUtil;
        this.commonService = commonService;
    }
    returnCheckout(title) {
        try {
            this.checkoutMatched = this.checkoutUtil.getCheckoutByTitle(title);
        }
        catch (error) {
            if (error.message.includes(CommonServiceError.noElement)) {
                this.commonService.pushErrorNotification(CheckoutUtilPhrases.itemNoCheckout);
                return;
            }
            throw error;
        }
        this.checkoutUtil.returnCheckout(this.userService.getUserId(), this.checkoutMatched.id);
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: CheckoutReturnService, deps: [{ token: Util.USER_SERVICE }, { token: CheckoutUtilService }, { token: CommonService }], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: CheckoutReturnService });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: CheckoutReturnService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [Util.USER_SERVICE]
                }] }, { type: CheckoutUtilService }, { type: CommonService }]; } });

class CheckoutRecordService {
    checkoutUtil;
    userService;
    recordUtil;
    collections;
    constructor(checkoutUtil, userService, recordUtil) {
        this.checkoutUtil = checkoutUtil;
        this.userService = userService;
        this.recordUtil = recordUtil;
    }
    recordCheckoutByTitle(title, fromScreen = 'INIT_SCREEN') {
        const record = this.recordUtil.getRecord(title);
        this.checkoutUtil.recordCheckout(record.id, record.title, this.userService.getUserId(), fromScreen);
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: CheckoutRecordService, deps: [{ token: CheckoutUtilService }, { token: Util.USER_SERVICE }, { token: RecordUtilService }], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: CheckoutRecordService });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: CheckoutRecordService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: CheckoutUtilService }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [Util.USER_SERVICE]
                }] }, { type: RecordUtilService }]; } });

class CheckoutDownloadService {
    checkoutUtil;
    commonService;
    userService;
    collections;
    constructor(checkoutUtil, commonService, userService) {
        this.checkoutUtil = checkoutUtil;
        this.commonService = commonService;
        this.userService = userService;
    }
    download(title) {
        const checkouts = this.checkoutUtil.getCheckouts();
        const checkout = this.commonService.findElementByTitle(title, checkouts);
        if (!checkout) {
            this.commonService.pushErrorNotification(CheckoutDownloadPhrases.noFound);
            throw new Error(CheckoutDownloadError.noFound);
        }
        const format = this.checkoutUtil.getFormatToDownload(checkout);
        const token = this.checkoutUtil.getToken(checkout.downloadUrl);
        this.checkoutUtil.downloadResource(checkout.id, this.userService.getUserId(), format, token);
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: CheckoutDownloadService, deps: [{ token: CheckoutUtilService }, { token: CommonService }, { token: Util.USER_SERVICE }], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: CheckoutDownloadService });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: CheckoutDownloadService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: CheckoutUtilService }, { type: CommonService }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [Util.USER_SERVICE]
                }] }]; } });

class CheckoutHandlerService {
    checkoutReturn;
    checkoutRecord;
    checkoutDownload;
    constructor(checkoutReturn, checkoutRecord, checkoutDownload) {
        this.checkoutReturn = checkoutReturn;
        this.checkoutRecord = checkoutRecord;
        this.checkoutDownload = checkoutDownload;
    }
    run(command) {
        if (!command.action) {
            throw new Error(CommandGuardsError.noAction);
        }
        switch (command.action) {
            case 'return':
                this.checkoutReturn.returnCheckout(Util.getTitle(command));
                break;
            case 'record':
                this.checkoutRecord.recordCheckoutByTitle(Util.getTitle(command));
                break;
            case 'download':
                this.checkoutDownload.download(Util.getTitle(command));
                break;
            default:
                throw new Error(`Command action ${command.action} not valid`);
        }
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: CheckoutHandlerService, deps: [{ token: CheckoutReturnService }, { token: CheckoutRecordService }, { token: CheckoutDownloadService }], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: CheckoutHandlerService });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: CheckoutHandlerService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: CheckoutReturnService }, { type: CheckoutRecordService }, { type: CheckoutDownloadService }]; } });

class CollectionsHandlerService {
    collectionsUtil;
    commonService;
    constructor(collectionsUtil, commonService) {
        this.collectionsUtil = collectionsUtil;
        this.commonService = commonService;
    }
    run(command) {
        const name = this.getNameCollection(command);
        const collections = this.collectionsUtil.getCollections();
        const collectionMatched = collections.find(collection => collection.topten === name);
        if (!collectionMatched) {
            this.commonService.pushErrorNotification(CollectionsPhrases.noCollections);
            console.error(`Collection: ${name} not found`);
        }
        const records = collectionMatched.records;
        if (!records) {
            throw new Error(RecordError.noRecords);
        }
        const titlesRecords = records.map(record => record.title);
        this.commonService.pushResultsNotification(titlesRecords);
    }
    getNameCollection(command) {
        if (!this.commandGuard(command)) {
            throw new Error(`Command: ${JSON.stringify(command)} not valid`);
        }
        return command.parameters.fields.collection.stringValue;
    }
    commandGuard(command) {
        return command
            && command.parameters
            && command.parameters.fields
            && command.parameters.fields.collection
            && command.parameters.fields.collection.stringValue;
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: CollectionsHandlerService, deps: [{ token: CollectionsUtilService }, { token: CommonService }], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: CollectionsHandlerService });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: CollectionsHandlerService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: CollectionsUtilService }, { type: CommonService }]; } });

class FavoriteService {
    userService;
    apiService;
    recordsUtil;
    commonService;
    collections;
    constructor(userService, apiService, recordsUtil, commonService) {
        this.userService = userService;
        this.apiService = apiService;
        this.recordsUtil = recordsUtil;
        this.commonService = commonService;
    }
    addFavorite(record) {
        if (this.getFavoriteByRecordId(record.id)) {
            console.log(`Record: ${record.id} its already favorite`);
            this.commonService.pushErrorNotification(FavoritesPhrases.itemIsFavorite);
            return;
        }
        this.apiService.addToFavorites(this.userService.getUserId(), record.id)
            .subscribe((fav) => {
            this.userService.favorites.unshift(fav);
            this.commonService.manageSuccesfulAction({
                phrase: FavoritesPhrases.added,
                notification: Util.ADD_FAVORITE_NOTIFICATION,
                path: Path.favorites,
            });
        }, err => console.error(err));
    }
    removeFavorite(record) {
        const favorite = this.getFavoriteByRecordId(record.id);
        if (!favorite) {
            this.commonService.pushErrorNotification(FavoritesPhrases.notFound);
            return;
        }
        this.apiService.removeFromFavorites(this.userService.getUserId(), favorite.recordId)
            .subscribe(() => {
            this.userService.removeFavorite(favorite);
            this.commonService.manageSuccesfulAction({
                phrase: FavoritesPhrases.removed,
                notification: Util.REMOVE_FAVORITE_NOTIFICATION,
                path: Path.favorites
            });
        }, err => console.error(err));
    }
    getFavoriteByTitle(title) {
        const record = this.recordsUtil.getRecord(title);
        return this.getFavoriteByRecordId(record.id);
    }
    getFavoriteByRecordId(recordId) {
        return this.userService.getFavoriteByRecordId(recordId);
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: FavoriteService, deps: [{ token: Util.USER_SERVICE }, { token: Util.API_SERVICE }, { token: RecordUtilService }, { token: CommonService }], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: FavoriteService });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: FavoriteService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [Util.USER_SERVICE]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [Util.API_SERVICE]
                }] }, { type: RecordUtilService }, { type: CommonService }]; } });

class HoldHandlerService {
    userService;
    apiService;
    recordUtil;
    commonService;
    constructor(userService, apiService, recordUtil, commonService) {
        this.userService = userService;
        this.apiService = apiService;
        this.recordUtil = recordUtil;
        this.commonService = commonService;
    }
    run(command) {
        if (!command.action) {
            throw new Error(CommandGuardsError.noAction);
        }
        switch (command.action) {
            case 'do':
                this.holdRecord(command);
                break;
            case 'cancel':
                this.cancelHold(command);
                break;
            default:
                throw new Error(`Command.action: ${command.action} is not valid`);
        }
    }
    holdRecord(command, record) {
        try {
            const recordDefault = this.getRecordDefault(record, command);
            const hold = this.getHold(recordDefault.id);
            if (hold) {
                this.commonService.pushErrorNotification('Ya tienes reservado ese artículo');
                return;
            }
            this.recordUtil.isAvailability(recordDefault.id)
                .then(availability => this.checkAvailability(availability, recordDefault))
                .catch(console.error);
        }
        catch (error) {
            throw error;
        }
    }
    checkAvailability(availability, record) {
        if (!availability) {
            this.apiService.holdRecord(this.userService.getUserId(), record.id, 'INIT_SCREEN')
                .subscribe((h) => {
                this.userService.holds.unshift(h);
                this.commonService.manageSuccesfulAction({
                    phrase: 'Artículo reservado',
                    notification: 'messages.holds.new',
                    path: Path.holds
                });
            }, err => {
                throw err;
            });
        }
    }
    cancelHold(command, record) {
        try {
            const recordDefault = this.getRecordDefault(record, command);
            const hold = this.getHold(recordDefault.id);
            if (!hold) {
                this.commonService.pushErrorNotification('No tienes reservado ese artículo');
                return;
            }
            this.apiService.cancelHold(hold.id).subscribe(() => {
                this.userService.removeHold(hold);
                this.commonService.manageSuccesfulAction({
                    phrase: 'Reserva cancelada',
                    notification: 'messages.holds.cancel',
                    path: Path.holds
                });
            }, err => {
                throw err;
            });
        }
        catch (error) {
            throw error;
        }
    }
    getRecordDefault(record, command) {
        if (!record) {
            try {
                return this.getRecord(this.getTitle(command));
            }
            catch (error) {
                this.commonService.pushErrorNotification('Ningun registro con ese título');
                console.error(error);
                return;
            }
        }
        return record;
    }
    getTitle(command) {
        return command.parameters.fields.title.stringValue;
    }
    getRecord(title) {
        const recordsResult = this.recordUtil.getRecords();
        return this.commonService.findElementByTitle(title, recordsResult);
    }
    getHold(checkoutId) {
        return this.userService.getHoldByRecordId(checkoutId);
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: HoldHandlerService, deps: [{ token: Util.USER_SERVICE }, { token: Util.API_SERVICE }, { token: RecordUtilService }, { token: CommonService }], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: HoldHandlerService });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: HoldHandlerService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [Util.USER_SERVICE]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [Util.API_SERVICE]
                }] }, { type: RecordUtilService }, { type: CommonService }]; } });

class RecordOpenInfoHandlerSercice {
    commonService;
    userService;
    checkoutUtil;
    favoriteService;
    holdService;
    recordUtil;
    record;
    url;
    constructor(commonService, userService, checkoutUtil, favoriteService, holdService, recordUtil) {
        this.commonService = commonService;
        this.userService = userService;
        this.checkoutUtil = checkoutUtil;
        this.favoriteService = favoriteService;
        this.holdService = holdService;
        this.recordUtil = recordUtil;
    }
    run(command) {
        if (this.record) {
            this.checkFollowUpContext(command);
            return;
        }
        try {
            this.open(command);
        }
        catch (error) {
            throw error;
        }
    }
    open(command) {
        this.record = this.recordUtil.getRecord(Util.getTitle(command));
        if (!this.record) {
            throw new Error(RecordError.noRecord);
        }
        this.url = `/info/${Util.slugifyRecord(this.record)}`;
        this.checkStateRecord();
        this.commonService.navigate(this.url);
    }
    checkStateRecord() {
        if (this.userService.getCheckoutByRecordId(this.record.id)) {
            this.commonService.pushInfoNotification(CheckoutGenericPhrases.itemCheckout);
            return;
        }
        this.commonService.pushInfoNotification(GenericPhrases.checkAvailability);
        this.recordUtil.isAvailability(this.record.id)
            .then(availability => this.handleResultCheckAvailability(availability))
            .catch(error => console.error(error));
    }
    handleResultCheckAvailability(availability) {
        if (availability) {
            this.commonService.pushInfoNotification(GenericPhrases.availabilityItem);
        }
        else {
            if (this.userService.getHoldByRecordId(this.record.id)) {
                this.commonService.pushInfoNotification(HoldsPhrases.reservated);
                return;
            }
            this.commonService.pushInfoNotification(GenericPhrases.noAvailability);
        }
    }
    checkFollowUpContext(command) {
        if (!command.action) {
            throw new Error(CommandGuardsError.noAction);
        }
        switch (command.action) {
            case 'read-description':
                this.readDescription();
                break;
            case 'record-checkout':
                this.recordCheckout();
                break;
            case 'return':
                this.returnCheckout();
                break;
            case 'download':
                this.download();
                break;
            case 'addFavorites':
                this.favoriteService.addFavorite(this.record);
                break;
            case 'removeToFavorites':
                this.favoriteService.removeFavorite(this.record);
                break;
            case 'doHold':
                this.holdService.holdRecord(command, this.record);
                break;
            case 'cancelHold':
                this.holdService.cancelHold(command, this.record);
                break;
            case 'list':
                this.handleList();
                break;
            default: throw new Error(`Command.action: ${command.action} not valid`);
        }
    }
    readDescription() {
        this.commonService.pushInfoNotification(this.record.description);
    }
    recordCheckout() {
        this.checkoutUtil.recordCheckout(this.record.id, this.record.title, this.userService.getUserId(), 'INIT_SCREEN');
    }
    returnCheckout() {
        const checkout = this.checkoutUtil.getCheckout(this.record.id);
        this.checkoutUtil.returnCheckout(this.userService.getUserId(), checkout.id);
    }
    download() {
        const checkout = this.checkoutUtil.getCheckout(this.record.id);
        const defaultFormat = this.checkoutUtil.getFormatToDownload(this.record);
        const token = this.checkoutUtil.getToken(checkout.downloadUrl);
        this.checkoutUtil.downloadResource(checkout.id, this.userService.getUserId(), defaultFormat, token);
    }
    handleList() {
        console.log('Handle list not implemented yet');
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: RecordOpenInfoHandlerSercice, deps: [{ token: CommonService }, { token: Util.USER_SERVICE }, { token: CheckoutUtilService }, { token: FavoriteService }, { token: HoldHandlerService }, { token: RecordUtilService }], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: RecordOpenInfoHandlerSercice });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: RecordOpenInfoHandlerSercice, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: CommonService }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [Util.USER_SERVICE]
                }] }, { type: CheckoutUtilService }, { type: FavoriteService }, { type: HoldHandlerService }, { type: RecordUtilService }]; } });

class FavoriteHandlerService {
    favoriteService;
    recordsUtil;
    constructor(favoriteService, recordsUtil) {
        this.favoriteService = favoriteService;
        this.recordsUtil = recordsUtil;
    }
    run(command) {
        const title = Util.getTitle(command);
        switch (command.action) {
            case 'add':
                this.favoriteService.addFavorite(this.recordsUtil.getRecord(title));
                break;
            case 'remove':
                this.favoriteService.removeFavorite(this.recordsUtil.getRecord(title));
                break;
            default: throw new Error(`Command.action: ${command.action} not valid`);
        }
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: FavoriteHandlerService, deps: [{ token: FavoriteService }, { token: RecordUtilService }], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: FavoriteHandlerService });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: FavoriteHandlerService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: FavoriteService }, { type: RecordUtilService }]; } });

class SearchService {
    apiService;
    localeService;
    recordUtil;
    commonService;
    router;
    zone;
    DEFAULT_ITEMS = 6;
    config;
    homeFilter;
    materias;
    currentSearch;
    lastSearch;
    constructor(apiService, localeService, recordUtil, commonService, router, zone) {
        this.apiService = apiService;
        this.localeService = localeService;
        this.recordUtil = recordUtil;
        this.commonService = commonService;
        this.router = router;
        this.zone = zone;
    }
    async searchByMateria(title) {
        this.config = await this.getConfiguration();
        this.homeFilter = this.config.homeFilter;
        this.materias = this.config.homeFilter.firstLevel.map(mat => mat.label);
        const materia = this.materias.find(mat => this.commonService.compareTitles(mat, title));
        this.search(`subject:${materia}`);
    }
    searchByTitle(title) {
        this.search(`title:${title}`);
    }
    searchByPublisher(publisher) {
        this.search(`publisher:${publisher}`);
    }
    searchByAuthor(author) {
        this.search(`author:${author}`);
    }
    searchAll(all) {
        this.search(`allfields_txt:${all}`);
    }
    search(query) {
        const requestTitle = this.buildRecordRequest(query);
        this.getRecordsApi(requestTitle);
        this.navigateToResultsComponent(requestTitle);
    }
    buildRecordRequest(query, offset = 0) {
        return {
            limit: this.DEFAULT_ITEMS,
            offset,
            query,
            exact: false,
            faceted: true,
            lists: true
        };
    }
    nextPage() {
        if (!this.currentSearch || !this.currentSearch.request) {
            this.commonService.pushErrorNotification(SearchPhrases.noPreviouSearch);
            return;
        }
        if (this.currentSearch.noResults) {
            this.commonService.pushErrorNotification(SearchPhrases.noMoreResults);
            return;
        }
        this.currentSearch.request.offset += this.DEFAULT_ITEMS;
        this.getRecordsApi(this.currentSearch.request);
        this.navigateToResultsComponent(this.currentSearch.request);
    }
    previousPage() {
        if (!this.lastSearch || !this.lastSearch.request || this.lastSearch.request.offset === 0) {
            this.commonService.pushErrorNotification(SearchPhrases.noPreviouSearch);
            return;
        }
        if (!this.lastSearch.results.length) {
            this.commonService.pushErrorNotification(SearchPhrases.noResultsPrevious);
            return;
        }
        this.lastSearch.request.offset -= this.DEFAULT_ITEMS;
        this.getRecordsApi(this.lastSearch.request);
        this.navigateToResultsComponent(this.lastSearch.request);
    }
    getRecordsApi(requestTitle) {
        this.apiService.getRecords(requestTitle)
            .subscribe((res) => {
            if (!res.records.length) {
                this.commonService.pushErrorNotification(SearchPhrases.noResults);
                return;
            }
            this.recordUtil.pushRecords(res.records);
            res.records.forEach((r) => this.commonService.pushInfoNotification(r.title));
            this.handleSearches(requestTitle, res.records);
        }, console.error);
    }
    handleSearches(request, results) {
        const noResults = !results.length ? true : false;
        const search = {
            request,
            results,
            noResults
        };
        if (!this.currentSearch && !this.lastSearch) {
            this.currentSearch = search;
            this.lastSearch = search;
            return;
        }
        if (this.currentSearch && this.currentSearch !== this.lastSearch) {
            this.lastSearch = this.currentSearch;
            this.currentSearch = search;
            return;
        }
        this.currentSearch = search;
    }
    navigateToResultsComponent(requestTitle) {
        this.zone.run(() => {
            this.router.navigate(['/results'], { queryParams: requestTitle })
                .catch(console.error);
        });
    }
    getConfiguration() {
        return new Promise((resolve, reject) => {
            this.apiService.getClientConfiguration(this.localeService.language)
                .subscribe(config => resolve(config), err => reject(err));
        });
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: SearchService, deps: [{ token: Util.API_SERVICE }, { token: Util.LOCALE_SERVICE }, { token: RecordUtilService }, { token: CommonService }, { token: i1$1.Router }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: SearchService });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: SearchService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [Util.API_SERVICE]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [Util.LOCALE_SERVICE]
                }] }, { type: RecordUtilService }, { type: CommonService }, { type: i1$1.Router }, { type: i0.NgZone }]; } });

class SearchHandlerService {
    searchService;
    constructor(searchService) {
        this.searchService = searchService;
    }
    run(command) {
        if (!command.parameters.fields) {
            throw new Error('Not found fields');
        }
        if (!command.action) {
            throw new Error('Command.action is required');
        }
        switch (command.action) {
            case 'allfields':
                this.searchService.searchAll(this.checkParamSearchAll(command));
                break;
            case 'search.title':
                this.searchService.searchByTitle(this.checkParamSearchTitle(command));
                break;
            case 'search.author':
                this.searchService.searchByAuthor(this.checkParamSearchAuthor(command));
                break;
            case 'search.publisher':
                this.searchService.searchByPublisher(this.checkParamSearchPublisher(command));
                break;
            case 'search.matter':
                this.searchService.searchByMateria(this.checkParamSearchSubject(command));
                break;
            case 'next.page':
                this.searchService.nextPage();
                break;
            case 'previous.page':
                this.searchService.previousPage();
                break;
            default: throw new Error(`Command.action ${command.action} is not valid`);
        }
    }
    checkParamSearchAll(command) {
        if (!command.parameters.fields.allfields) {
            throw new Error('Not found parameter allfields');
        }
        return command.parameters.fields.allfields.stringValue;
    }
    checkParamSearchTitle(command) {
        if (!command.parameters.fields.title) {
            throw new Error('Not found parameter title');
        }
        return command.parameters.fields.title.stringValue;
    }
    checkParamSearchAuthor(command) {
        if (!command.parameters.fields.author) {
            throw new Error('Not found parameter author');
        }
        return command.parameters.fields.author.stringValue;
    }
    checkParamSearchPublisher(command) {
        if (!command.parameters.fields.publisher) {
            throw new Error('Not found parameter publisher');
        }
        return command.parameters.fields.publisher.stringValue;
    }
    checkParamSearchSubject(command) {
        if (!command.parameters.fields.subject) {
            throw new Error('Not found parameter subject');
        }
        return command.parameters.fields.subject.stringValue;
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: SearchHandlerService, deps: [{ token: SearchService }], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: SearchHandlerService });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: SearchHandlerService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: SearchService }]; } });

class NgxHandlerService {
    navigateHandler;
    checkoutHandler;
    collectionsHandler;
    openInfoRecordHandler;
    holdHandler;
    favoriteHandler;
    searchHandler;
    intent;
    constructor(navigateHandler, checkoutHandler, collectionsHandler, openInfoRecordHandler, holdHandler, favoriteHandler, searchHandler) {
        this.navigateHandler = navigateHandler;
        this.checkoutHandler = checkoutHandler;
        this.collectionsHandler = collectionsHandler;
        this.openInfoRecordHandler = openInfoRecordHandler;
        this.holdHandler = holdHandler;
        this.favoriteHandler = favoriteHandler;
        this.searchHandler = searchHandler;
    }
    resolve(command) {
        this.checkParamsRequired(command);
        if (this.intent.includes('checkout')) {
            return this.checkoutHandler;
        }
        else if (this.intent.includes('hold')) {
            return this.holdHandler;
        }
        else if (this.intent.includes('search')) {
            return this.searchHandler;
        }
        else if (this.intent.includes('open')) {
            return this.openInfoRecordHandler;
        }
        else if (this.intent.includes('favorite')) {
            return this.favoriteHandler;
        }
        else if ('navigate' === this.intent) {
            return this.navigateHandler;
        }
        else if ('collection' === this.intent) {
            return this.collectionsHandler;
        }
        else if ('reload' === this.intent) {
            const currentPath = window.location.href;
            const replace = (url) => url.replace(/\/user.*/g, '');
            window.location.href = replace(currentPath);
        }
        else {
            throw new Error(HandlerServiceError.noIntentFound);
        }
    }
    checkParamsRequired(command) {
        if (!command || !command.intentDisplayName) {
            throw new Error(HandlerError.ParamsHandlerResolver);
        }
        this.intent = command.intentDisplayName;
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: NgxHandlerService, deps: [{ token: NavigateHandler }, { token: CheckoutHandlerService }, { token: CollectionsHandlerService }, { token: RecordOpenInfoHandlerSercice }, { token: HoldHandlerService }, { token: FavoriteHandlerService }, { token: SearchHandlerService }], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: NgxHandlerService });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: NgxHandlerService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: NavigateHandler }, { type: CheckoutHandlerService }, { type: CollectionsHandlerService }, { type: RecordOpenInfoHandlerSercice }, { type: HoldHandlerService }, { type: FavoriteHandlerService }, { type: SearchHandlerService }]; } });

class NgxDispatcherService {
    handler;
    commonService;
    constructor(handler, commonService) {
        this.handler = handler;
        this.commonService = commonService;
    }
    dispatch(command) {
        try {
            const handlerResolver = this.handler.resolve(command);
            handlerResolver.run(command);
        }
        catch (error) {
            this.commonService.pushErrorNotification("Lo siento, no he podido hacer eso");
            console.error(error);
        }
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: NgxDispatcherService, deps: [{ token: NgxHandlerService }, { token: CommonService }], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: NgxDispatcherService });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: NgxDispatcherService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: NgxHandlerService }, { type: CommonService }]; } });

class NgxAssistantOpacComponent {
    userService;
    recordUtil;
    syncDataService;
    commandAssistantService;
    configService;
    dispatcherService;
    collectionsLoaded;
    destroy$ = new Subject();
    constructor(userService, recordUtil, syncDataService, commandAssistantService, configService, dispatcherService) {
        this.userService = userService;
        this.recordUtil = recordUtil;
        this.syncDataService = syncDataService;
        this.commandAssistantService = commandAssistantService;
        this.configService = configService;
        this.dispatcherService = dispatcherService;
        this.syncDataService.config$
            .pipe(takeUntil(this.destroy$))
            .subscribe((conf) => {
            this.configService.config = conf;
        });
    }
    ngOnInit() {
        this.syncDataService.userData$
            .pipe(takeUntil(this.destroy$))
            .subscribe((userData) => {
            if (!this.collectionsLoaded) {
                this.recordUtil.loadCollections();
                this.collectionsLoaded = true;
            }
            this.loadData(userData);
        });
        this.commandAssistantService
            .getCommandStream()
            .pipe(takeUntil(this.destroy$))
            .subscribe((command) => {
            try {
                this.dispatcherService.dispatch(command);
            }
            catch (error) {
                throw new Error(`Error subscription from commandAssistantService: ${error.message}`);
            }
        });
    }
    loadData(userData) {
        if (this.validateData(userData.checkouts)) {
            this.userService.checkouts = userData.checkouts;
            this.recordUtil.loadDataInRecords(userData.checkouts);
        }
        if (this.validateData(userData.holds)) {
            this.userService.holds = userData.holds;
            this.recordUtil.loadDataInRecords(userData.holds);
        }
        if (this.validateData(userData.favorites)) {
            this.userService.favorites = userData.favorites;
            this.recordUtil.loadDataInRecords(userData.favorites);
        }
        if (this.validateData(userData.lists)) {
            this.userService.lists = userData.lists;
            const lists = userData.lists.map((list) => list.items);
            const items = [].concat.apply([], lists);
            this.recordUtil.loadDataInRecords(items);
        }
    }
    validateData(data) {
        return data && data.length;
    }
    ngOnDestroy() {
        this.destroy$.next(true);
        this.destroy$.unsubscribe();
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: NgxAssistantOpacComponent, deps: [{ token: Util.USER_SERVICE }, { token: RecordUtilService }, { token: NgxSyncDataService }, { token: NgxCommandService }, { token: NgxConfigService }, { token: NgxDispatcherService }], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: NgxAssistantOpacComponent, selector: "lib-ngx-assistant-opac", ngImport: i0, template: `<lib-ngx-assistant></lib-ngx-assistant>`, isInline: true, dependencies: [{ kind: "component", type: NgxAssistantComponent, selector: "lib-ngx-assistant" }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: NgxAssistantOpacComponent, decorators: [{
            type: Component,
            args: [{
                    selector: "lib-ngx-assistant-opac",
                    template: `<lib-ngx-assistant></lib-ngx-assistant>`,
                }]
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [Util.USER_SERVICE]
                }] }, { type: RecordUtilService }, { type: NgxSyncDataService }, { type: NgxCommandService }, { type: NgxConfigService }, { type: NgxDispatcherService }]; } });

class NgxAssistantOpacModule {
    static forRoot(apiService, userService, localeService) {
        return {
            ngModule: NgxAssistantOpacModule,
            providers: [
                { provide: "ApiServiceOpac", useClass: apiService },
                { provide: "UserServiceOpac", useClass: userService },
                { provide: "LocalServiceOpac", useClass: localeService },
            ],
        };
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: NgxAssistantOpacModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.2.12", ngImport: i0, type: NgxAssistantOpacModule, declarations: [NgxAssistantOpacComponent], imports: [NgxAssistantModule], exports: [NgxAssistantOpacComponent] });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: NgxAssistantOpacModule, providers: [
            NgxDispatcherService,
            NgxHandlerService,
            CommonService,
            SearchHandlerService,
            SearchService,
            RecordOpenInfoHandlerSercice,
            RecordUtilService,
            NavigateHandler,
            PageFactoryService,
            HoldHandlerService,
            FavoriteHandlerService,
            FavoriteService,
            CollectionsHandlerService,
            CollectionsUtilService,
            CheckoutDownloadService,
            CheckoutHandlerService,
            CheckoutRecordService,
            CheckoutReturnService,
            CheckoutUtilService,
        ], imports: [NgxAssistantModule] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: NgxAssistantOpacModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [NgxAssistantModule],
                    declarations: [NgxAssistantOpacComponent],
                    providers: [
                        NgxDispatcherService,
                        NgxHandlerService,
                        CommonService,
                        SearchHandlerService,
                        SearchService,
                        RecordOpenInfoHandlerSercice,
                        RecordUtilService,
                        NavigateHandler,
                        PageFactoryService,
                        HoldHandlerService,
                        FavoriteHandlerService,
                        FavoriteService,
                        CollectionsHandlerService,
                        CollectionsUtilService,
                        CheckoutDownloadService,
                        CheckoutHandlerService,
                        CheckoutRecordService,
                        CheckoutReturnService,
                        CheckoutUtilService,
                    ],
                    exports: [NgxAssistantOpacComponent],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { NgxAssistantComponent, NgxAssistantModule, NgxAssistantOpacComponent, NgxAssistantOpacModule, NgxCommandService, NgxErrorService, NgxNotificationService, NgxSyncDataService };
